import {
  BookOpen,
  Copy,
  ExternalLink,
  FileStack,
  FileText,
  HardHat,
  ScanEye,
  Scale,
  StickyNote,
  type LucideIcon,
} from "lucide-react";
import { Link, useLocation } from "react-router";
import {
  CHAT_SOURCE_LABELS,
  type ChatSource,
  type ChatSourceKind,
} from "@gb/contracts";
import type { CameFrom } from "@/features/brain/file-link";
import { cn } from "@/lib/utils";
import { linkFor } from "./chat-links";

/**
 * One source behind an answer.
 *
 * The number is the anchor: it is what the citation chip in the answer shows,
 * and clicking that chip scrolls this card into view. So the number is the
 * loudest thing on it, and the card carries an id the answer can target.
 *
 * Two links, because a reader checking a claim asks two questions in order:
 * *what did it say* — the passage, which is the whole card — and *show me it in
 * the document*, which is the small link at the bottom and only appears when
 * there is a file behind the source.
 */

/**
 * The glyph for each kind of source.
 *
 * Exported because `brain/decision-dialog.tsx` renders the same union and had
 * grown its own map, which disagreed on three of the six: a `document` was a
 * FileStack here and a FileText there, a `note` a StickyNote here and a FileText
 * there, and `crew` a HardHat against a Users. One vocabulary, one glyph per
 * member — the labels already come from `CHAT_SOURCE_LABELS` in the contract,
 * and the icons cannot follow them there because React Native has no
 * `lucide-react`.
 */
export const KIND_ICONS: Record<ChatSourceKind, LucideIcon> = {
  document: FileStack,
  note: StickyNote,
  manual: BookOpen,
  decision: Scale,
  journal: FileText,
  crew: HardHat,
};

export function sourceAnchorId(n: number): string {
  return `chat-source-${n}`;
}

/**
 * What the card says about when a source is from.
 *
 * A file that states no date carries its upload day, and an upload day is not
 * a date the file is from: a bundle arrives at one moment (`document-ai/0016`
 * §5). So an uploaded day says so, "Undated (uploaded …)" (R87, 2026-09-26).
 * An answer stored before `dateKind` shows its date as before.
 *
 * **Every screen that dates a source uses this** (2026-09-27). The decision
 * dialog lists the sources of the answer a decision came from, and printed
 * an upload day there as the date the file is from. `format` writes the day
 * as that screen writes its dates; the card shows it as stored.
 */
export function sourceDateLabel(
  source: Pick<ChatSource, "date" | "dateKind">,
  format: (day: string) => string = (day) => day,
): string | null {
  if (!source.date) return null;
  const day = format(source.date);
  return source.dateKind === "uploaded" ? `Undated (uploaded ${day})` : day;
}

/** "Also filed as: name (folder)", one per identical copy (R92). Names and folders as filed. */
export function copiesLine(copies: NonNullable<ChatSource["copies"]>): string {
  return copies.map((copy) => (copy.folder ? `${copy.name} (${copy.folder})` : copy.name)).join(", ");
}

export function SourceCard({
  source,
  projectId,
  highlighted,
  onFindStep,
}: {
  source: ChatSource;
  projectId: string;
  /** True just after its citation chip was clicked. */
  highlighted: boolean;
  /**
   * Opens the answer's steps at one step: "Found in step 4" (2026-09-26, Phase
   * 5). Absent where there is no list of steps to open.
   */
  onFindStep?: (seq: number) => void;
}) {
  const Icon = KIND_ICONS[source.kind];
  const location = useLocation();
  // The entry and file screens read this, so their back link returns to the
  // answer rather than to the Brain.
  const from: CameFrom = { path: `${location.pathname}${location.search}`, label: "Back to the answer" };
  const target = linkFor(projectId, source.link);
  const original = source.original ? linkFor(projectId, source.original) : null;
  const dateLabel = sourceDateLabel(source);
  // The first step that returned it. Several steps can return one source (a
  // search snippet, then the passage opened); the first is where it was found.
  const foundIn = source.steps && source.steps.length > 0 ? Math.min(...source.steps) : null;

  return (
    <div
      id={sourceAnchorId(source.n)}
      className={cn(
        "scroll-mt-4 rounded-card-lg border-[1.5px] bg-surface p-3.5 transition-colors",
        highlighted ? "border-brand bg-brand/5" : "border-border",
      )}
    >
      <div className="mb-1.5">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "flex size-[20px] shrink-0 items-center justify-center rounded-full",
              "border border-brand/40 bg-brand/10 font-display text-[0.66rem] font-semibold text-brand",
            )}
          >
            {source.n}
          </span>
          <Icon size={13} className="shrink-0 text-muted" />
          <span className="font-display text-[0.62rem] font-semibold tracking-[0.08em] text-muted uppercase">
            {CHAT_SOURCE_LABELS[source.kind]}
          </span>
        </div>
        {/* The date earns its place on the card: an answer that says one source
            superseded another is checkable only if the reader can see the same
            ordering it reasoned over. On its own line under the kind, because
            "Undated (uploaded …)" does not fit beside it in the sources rail and
            was cut off at the card's edge (2026-09-27). */}
        {dateLabel && <p className="mt-1 text-[0.72rem] text-muted">{dateLabel}</p>}
      </div>

      <Link
        to={target.href}
        state={{ from }}
        className="block font-semibold text-[0.88rem] leading-snug text-ink hover:text-brand"
      >
        {source.title}
      </Link>
      <p className="mt-0.5 text-[0.75rem] text-muted">{source.citation}</p>
      <p className="mt-1.5 line-clamp-3 text-[0.8rem] leading-[1.5] text-ink-slate">
        {source.snippet}
      </p>

      {/* A value a look read that the page's text does not hold (R62). The
          reader checks it on the picture, not by searching the text. */}
      {source.imageOnly && (
        <p className="mt-1.5 inline-flex items-center gap-1 rounded-tag bg-warn-bg px-1.5 py-0.5 text-[0.72rem] font-semibold text-warn">
          <ScanEye size={12} aria-hidden />
          Seen in the image only
        </p>
      )}

      {source.copies && source.copies.length > 0 && (
        <p className="mt-1.5 flex items-start gap-1 text-[0.72rem] text-muted">
          <Copy size={12} className="mt-[2px] shrink-0" aria-hidden />
          <span>Also filed as: {copiesLine(source.copies)}</span>
        </p>
      )}

      {/* Where the answer found it, and a note that cited it (2026-09-26,
          Phase 5). Quiet: the card is about the passage, and these say how
          the answer came by it. */}
      {onFindStep && (foundIn !== null || source.noteStep !== undefined) && (
        <p className="mt-1.5 flex flex-wrap gap-x-3 text-[0.72rem]">
          {foundIn !== null && (
            <button
              type="button"
              onClick={() => onFindStep(foundIn)}
              className="cursor-pointer text-muted underline-offset-2 hover:text-brand hover:underline"
            >
              Found in step {foundIn}
            </button>
          )}
          {source.noteStep !== undefined && (
            <button
              type="button"
              onClick={() => onFindStep(source.noteStep!)}
              className="cursor-pointer text-muted underline-offset-2 hover:text-brand hover:underline"
            >
              Cited in the note at step {source.noteStep}
            </button>
          )}
        </p>
      )}

      {original && (
        <Link
          // A `Link`, not a plain anchor: this used to point at the presigned
          // download route and had to be a browser navigation — which never
          // worked, because that route is behind the guard and a navigation
          // carries no bearer token. It now opens the viewer, an in-app route.
          //
          // Still a new tab: the reader asked a question and is reading the
          // answer, and going to look at page 23 should not cost them the
          // conversation they are in the middle of.
          to={original.href}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[0.75rem] font-semibold text-muted transition-colors hover:text-brand"
        >
          <ExternalLink size={12} />
          Open the file
        </Link>
      )}
    </div>
  );
}
